import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import { ArrowUpRight } from "lucide-react";

const IMAGES = [
  "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?auto=format&fit=crop&w=1200&q=80",
  "https://images.pexels.com/photos/20998162/pexels-photo-20998162.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  "/img/destinations/shanghai.png",
  "/img/destinations/xian.png",
  "/img/destinations/zhangjiajie.png",
];

const Destinations = () => {
  const { t } = useLang();
  const items = t.destinations.items;

  // Desktop bento layout — 6 cards with varied sizes
  const layouts = [
    "md:col-span-7 md:row-span-2",
    "md:col-span-5",
    "md:col-span-5",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-4",
  ];

  return (
    <section
      id="destinations"
      data-testid="destinations-section"
      className="bg-[#F7F5F0] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionReveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">
            ✦ {t.destinations.eyebrow}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] max-w-3xl">
            {t.destinations.title}
          </h2>
        </SectionReveal>

        {/* Mobile: horizontal carousel. Desktop: bento grid */}
        <div
          data-testid="destinations-carousel-mobile"
          className="mt-12 md:hidden -mx-6 px-6 overflow-x-auto snap-x snap-mandatory flex gap-4 pb-4 scrollbar-hide"
        >
          {items.map((d, i) => (
            <DestinationCard
              key={d.name}
              data={d}
              image={IMAGES[i]}
              index={i}
              className="snap-center flex-shrink-0 w-[85%] aspect-[3/4]"
            />
          ))}
        </div>

        <div className="mt-16 hidden md:grid md:grid-cols-12 gap-5 md:gap-6 auto-rows-[260px]">
          {items.map((d, i) => (
            <DestinationCard
              key={d.name}
              data={d}
              image={IMAGES[i]}
              index={i}
              className={layouts[i]}
              reveal
            />
          ))}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

const DestinationCard = ({ data, image, index, className = "", reveal = false }) => {
  const inner = (
    <div className={`relative overflow-hidden group cursor-pointer border border-[#EAE6DF] ${reveal ? "h-full w-full" : className}`}>
      <div
        data-testid={`destination-card-${index}`}
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] ease-out group-hover:scale-110"
        style={{ backgroundImage: `url(${image})` }}
      />
      {/* Always-visible gradient overlay so text is always legible */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent" />

      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-white">
        <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
          {data.city}
        </p>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-2xl md:text-3xl leading-tight">{data.name}</h3>
          <span className="w-9 h-9 flex-shrink-0 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full flex items-center justify-center group-hover:bg-[#C62828] group-hover:border-[#C62828] transition-all duration-300">
            <ArrowUpRight size={15} className="text-white" />
          </span>
        </div>
        {/* Always-visible blurb (no longer hover-only) */}
        <p className="mt-3 text-sm text-white/85 max-w-md leading-relaxed">{data.blurb}</p>
      </div>
    </div>
  );

  if (reveal) {
    return (
      <SectionReveal delay={index * 0.08} className={className}>
        {inner}
      </SectionReveal>
    );
  }
  return inner;
};

export default Destinations;
